import { useCategories } from "@/api/cms";
import { useSaneModifiableList } from "@/api/modifiableList";
import { constructLocaleDict, getMainLanguage, SupportedLocale } from "@/i18n";
import {
  CatalogueRaisonneSection,
  CatalogueRaisonneSectionUpsert,
} from "@/lib/cms/cms-types";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { generateImageName } from "@/lib/utils";
import { useGetAttachmentById } from "@/api/attachment";
import useMediaAttachment from "@/hooks/useMediaAttachment";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  GripVertical,
  Image as ImageIcon,
  Pencil,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Path, useFieldArray, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CroppedImageInput } from "../form-fields/CroppedImageInput";
import * as AF from "../form-items/AbstractForm";
import { ModifiableList } from "../form-items/ModifiableList";
import { MultilingualField } from "../form-items/MultilingualField";
import { MediaPreview } from "../media/MediaPreview";
import StyledDrawer from "../StyledDrawer";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Sortable, SortableDragHandle, SortableItem } from "../ui/sortable";

const SORT_VALUES_EXCLUDED_FOR_MULTIPRINTS: string[] = [
  "categorie:asc",
  "categorie:desc",
  "serie:asc",
  "serie:desc",
  "themes:asc",
  "themes:desc",
];

// Les multiples n'ont ni catégorie, ni série, ni thème sur leur fiche.
const DECOUPAGE_TYPES_EXCLUDED_FOR_MULTIPRINTS: string[] = [
  "categories",
  "series",
  "themes",
];

type DecoupageFormValues = {
  decoupages?: CatalogueRaisonneSectionUpsert[];
  type?: string | undefined;
  target?: "ARTWORKS" | "MULTIPRINTS" | undefined;
  sortByInsideDecoupage?: string;
};

const TypeOeuvreTextField = ({
  name,
  label,
  lang,
  categoryId,
  form,
  tooltipText,
}: {
  name: string;
  label: string;
  lang: SupportedLocale;
  categoryId: string | undefined;
  form: UseFormReturn<any>;
  tooltipText?: string;
}) => {
  const mainLang = getMainLanguage();
  const { data: mainLangData } = useSaneModifiableList(
    "liste-types-oeuvres",
    mainLang,
  );

  const matchingOrder = useMemo(() => {
    if (!categoryId || !mainLangData) return null;
    const match = mainLangData.find((item) => item.value === categoryId);
    return match?.order ?? null;
  }, [categoryId, mainLangData]);

  const prefillValue = useMemo(() => {
    if (matchingOrder === null) return "";
    return (
      mainLangData?.find((item) => item.order === matchingOrder)?.value ?? ""
    );
  }, [matchingOrder, mainLangData]);

  useEffect(() => {
    if (prefillValue && categoryId) {
      form.setValue(name, prefillValue, { shouldDirty: false });
    }
  }, [prefillValue, categoryId, name, form]);

  return (
    <ModifiableList
      listKey="liste-types-oeuvres"
      name={name}
      label={label}
      placeholder={label}
      lang={lang}
      tooltipText={tooltipText}
    />
  );
};

type DecoupageTableVignetteProps = {
  index: number;
  target: "ARTWORKS" | "MULTIPRINTS";
  cmsID: string;
  form: UseFormReturn<DecoupageFormValues>;
};

const DecoupageTableVignette = ({
  index,
  target,
  cmsID,
  form,
}: DecoupageTableVignetteProps) => {
  const formVignette = form.watch(`decoupages.${index}.vignette`);
  const imagePosition = `decoupage${target}-${index}`;

  const { attachment: polledByPosition, previewImageUrl: polledPreviewUrl } =
    useMediaAttachment(imagePosition, cmsID);

  const { data: polledById } = useGetAttachmentById(formVignette?.id ?? "");

  const resolvedAttachment =
    polledById ?? polledByPosition ?? formVignette ?? undefined;
  const previewUrl =
    resolvedAttachment?.mediumPublicUrl ??
    resolvedAttachment?.posterPublicUrl ??
    resolvedAttachment?.smallPublicUrl ??
    resolvedAttachment?.publicUrl ??
    polledPreviewUrl ??
    null;

  if (previewUrl) {
    return (
      <img
        src={previewUrl}
        alt=""
        className="h-12 w-[72px] rounded-md border border-border object-cover"
      />
    );
  }

  return (
    <div
      className="flex h-12 w-[72px] items-center justify-center rounded-md border border-dashed border-border bg-muted/30"
      aria-hidden="true"
    >
      <ImageIcon className="h-4 w-4 text-muted-foreground" />
    </div>
  );
};

type DecoupageDrawerEditorProps = {
  index: number;
  form: UseFormReturn<DecoupageFormValues>;
  target: "ARTWORKS" | "MULTIPRINTS";
  cmsID: string;
  showCategoryFields: boolean;
  selectedType: string | undefined;
  categoriesData: { value: string }[] | undefined;
  isLoadingCategories: boolean;
  categoryLabel: string;
};

const DecoupageDrawerEditor = ({
  index,
  form,
  target,
  cmsID,
  showCategoryFields,
  selectedType,
  categoriesData,
  isLoadingCategories,
  categoryLabel,
}: DecoupageDrawerEditorProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const isMultiprints = target === "MULTIPRINTS";
  const typeTooltip = isMultiprints
    ? vt(
        "bo.cms.decoupage.typeMultiprintTooltip",
        "Indiquez ici le type d'estampe / multiple dans les différentes langues",
      )
    : t("typeOeuvreTooltip");
  const categoryPlaceholder =
    isMultiprints && selectedType === "type"
      ? vt(
          "bo.cms.decoupage.selectTypeMultiprint",
          "Sélectionner un type d'estampe / multiple",
        )
      : t(
          "select" +
            selectedType?.charAt(0).toUpperCase() +
            selectedType?.slice(1),
        );
  const { watch } = form;
  const imagePosition = `decoupage${target}-${index}`;
  const formVignette = watch(`decoupages.${index}.vignette`);

  const {
    attachment: polledByPosition,
    previewImageUrl: polledPreviewUrl,
    refetch: refetchByPosition,
  } = useMediaAttachment(imagePosition, cmsID);

  const { data: polledById, refetch: refetchById } = useGetAttachmentById(
    formVignette?.id ?? "",
    { pollWhileProcessing: true },
  );

  const resolvedAttachment =
    polledById ?? polledByPosition ?? formVignette ?? undefined;
  const resolvedPreviewUrl =
    resolvedAttachment?.mediumPublicUrl ??
    resolvedAttachment?.posterPublicUrl ??
    polledPreviewUrl;

  const refetchVignette = useCallback(() => {
    void refetchByPosition();
    if (formVignette?.id) void refetchById();
  }, [refetchByPosition, refetchById, formVignette?.id]);

  useEffect(() => {
    if (!polledByPosition?.id && !polledById?.id) return;

    const latest = polledById ?? polledByPosition;
    if (!latest?.id) return;

    const current = form.getValues(`decoupages.${index}.vignette`);
    if (current?.id !== latest.id) return;

    // Ne PAS comparer les URLs signées : chaque refetch change la signature et
    // relançait setValue → remount → refetch (boucle vignettes CR).
    const hasStructuralMediaChange =
      latest.kind !== current?.kind ||
      latest.videoStatus !== current?.videoStatus ||
      (!!latest.videoPublicUrl && !current?.videoPublicUrl) ||
      (!!latest.posterPublicUrl &&
        !current?.posterPublicUrl &&
        latest.kind === "video");

    if (hasStructuralMediaChange) {
      form.setValue(
        `decoupages.${index}.vignette`,
        {
          ...latest,
          applyWatermark: latest.applyWatermark ?? false,
        },
        { shouldDirty: true },
      );
    }
  }, [polledByPosition, polledById, form, index]);

  return (
    <AF.Col className="gap-4">
      {showCategoryFields ? (
        <AF.SelectField
          label={""}
          name={`decoupages.${index}.categoryId`}
          options={
            categoriesData?.map((category) => ({
              value: category.value,
              label: category.value,
            })) ?? []
          }
          placeholder={categoryPlaceholder}
          disabled={isLoadingCategories}
        />
      ) : (
        <AF.Row>
          <AF.Text
            name={`decoupages.${index}.startValue`}
            label={
              selectedType === "creationDate" ? t("startYear") : t("startNumber")
            }
            placeholder={
              selectedType === "creationDate" ? t("startYear") : t("startNumber")
            }
          />
          <AF.Text
            name={`decoupages.${index}.endValue`}
            label={selectedType === "creationDate" ? t("endYear") : t("endNumber")}
            placeholder={
              selectedType === "creationDate" ? t("endYear") : t("endNumber")
            }
          />
        </AF.Row>
      )}

      <AF.Checkbox
        name={`decoupages.${index}.showContent`}
        field={{
          defaultValue: watch(`decoupages.${index}.showContent`) ?? false,
        }}
        label={t("catalogueRaisonne.decoupage.showContent")}
      />

      <MultilingualField
        fixed={getMainLanguage()}
        render={(lang) => {
          const categoryId = watch(`decoupages.${index}.categoryId`) ?? undefined;
          const showContent = watch(`decoupages.${index}.showContent`) ?? false;
          return (
            <div className="flex flex-col gap-2">
              {selectedType === "type" && (
                <TypeOeuvreTextField
                  name={`decoupages.${index}.categoryMultilingual.${lang}`}
                  label={categoryLabel}
                  lang={lang}
                  categoryId={categoryId}
                  form={form}
                  tooltipText={typeTooltip}
                />
              )}
              <AF.Text
                name={`decoupages.${index}.nameMultilingual.${lang}`}
                label={t("sectionName")}
                placeholder={t("sectionName")}
              />
              {showContent && (
                <div className="flex flex-col gap-2">
                  <AF.Text
                    name={`decoupages.${index}.titleMultilingual.${lang}`}
                    label={t("titleSection")}
                    placeholder={t("titleSection")}
                  />
                  <AF.RichText
                    name={`decoupages.${index}.contentMultilingual.${lang}`}
                    label={t("catalogueRaisonne.decoupage.presentationText")}
                  />
                </div>
              )}
            </div>
          );
        }}
      />
      <MultilingualField
        excludeLang={getMainLanguage()}
        render={(lang) => {
          const showContent = watch(`decoupages.${index}.showContent`) ?? false;
          return (
            <div className="flex flex-col gap-2">
              {selectedType === "type" && (
                <ModifiableList
                  listKey="liste-types-oeuvres"
                  name={`decoupages.${index}.categoryMultilingual.${lang}`}
                  label={categoryLabel}
                  placeholder={categoryLabel}
                  lang={lang}
                  tooltipText={typeTooltip}
                />
              )}
              <AF.Text
                name={`decoupages.${index}.nameMultilingual.${lang}`}
                label={t("sectionName")}
                placeholder={t("sectionName")}
              />
              {showContent && (
                <div className="flex flex-col gap-2">
                  <AF.Text
                    name={`decoupages.${index}.titleMultilingual.${lang}`}
                    label={t("titleSection")}
                    placeholder={t("titleSection")}
                  />
                  <AF.RichText
                    name={`decoupages.${index}.contentMultilingual.${lang}`}
                    label={t("catalogueRaisonne.decoupage.presentationText")}
                  />
                </div>
              )}
            </div>
          );
        }}
      />

      <Separator className="my-2" />

      <AF.Col>
        <p className="block text-sm font-medium mb-2">{t("vignetteImage")}</p>
        <div className="flex gap-2 items-stretch w-full">
          <CroppedImageInput
            inlineActions
            buttonSize="sm"
            attachment={resolvedAttachment}
            imageURL={resolvedPreviewUrl}
            refetch={refetchVignette}
            imagePosition={imagePosition}
            docId={cmsID}
            aspect={1.5}
            forceCreateNewAttachment={true}
            buildImageName={(instanceName) =>
              generateImageName({
                primaryType: "thumbnail",
                instanceName,
                pageNameInDefaultLanguage: "CatalogueRaisonne",
                title: `decoupage-${index}`,
              })
            }
            onSuccess={(attachment) => {
              if (!attachment.id) return;
              form.setValue(
                `decoupages.${index}.vignette`,
                {
                  ...attachment,
                  id: attachment.id,
                  applyWatermark: attachment.applyWatermark ?? false,
                  photoCredits: attachment.photoCredits ?? "",
                },
                { shouldDirty: true },
              );
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1 min-w-0 font-semibold"
            disabled={!resolvedAttachment}
            onClick={() => {
              form.setValue(`decoupages.${index}.vignette`, null, {
                shouldDirty: true,
              });
            }}
          >
            {t("removePhoto")}
          </Button>
        </div>
        <MediaPreview
          attachment={resolvedAttachment}
          previewImageUrl={resolvedPreviewUrl}
        />
      </AF.Col>
    </AF.Col>
  );
};

const DecoupageCatalogueRaisonneListForm = ({
  form,
  target,
  sections,
  cmsID,
}: {
  form: UseFormReturn<DecoupageFormValues>;
  target: "ARTWORKS" | "MULTIPRINTS";
  sections: CatalogueRaisonneSection[];
  cmsID: string;
}) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { control, watch } = form;
  const mainLang = getMainLanguage();

  const selectableCategoryTypes = ["categories", "series", "themes", "type"];
  const selectedType = watch("type");
  const showCategoryFields = selectableCategoryTypes.includes(
    selectedType as string,
  );
  const categoryLabel =
    target === "MULTIPRINTS"
      ? vt(
          "bo.cms.decoupage.typeMultiprintForSection",
          "Type d'estampe / multiple pour cette section",
        )
      : t("categoryLabel", {
          defaultValue: t("categoryLabel"),
        });
  const selectTypePlaceholder =
    target === "MULTIPRINTS"
      ? vt(
          "bo.cms.decoupage.selectTypeMultiprint",
          "Sélectionner un type d'estampe / multiple",
        )
      : t("selectType");

  const { data: categoriesData, isLoading: isLoadingCategories } =
    useCategories(selectedType);

  const { fields, append, remove, replace, move } = useFieldArray({
    control,
    name: "decoupages",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [confirmingIndex, setConfirmingIndex] = useState<number | null>(null);
  const [openLastAfterAppend, setOpenLastAfterAppend] = useState(false);

  const handleMove = useCallback(
    ({
      activeIndex,
      overIndex,
    }: {
      activeIndex: number;
      overIndex: number;
    }) => {
      move(activeIndex, overIndex);
      const decoupages = form.getValues("decoupages");
      if (decoupages) {
        decoupages.forEach((_, i) => {
          form.setValue(`decoupages.${i}.order`, i + 1, { shouldDirty: true });
        });
      }
    },
    [move, form],
  );

  // Empreinte stable (ids) : évite replace() à chaque refetch de `sections`.
  const sectionsFingerprint = useMemo(
    () =>
      sections
        .map((s) => `${s.id ?? ""}:${s.type ?? ""}:${s.order ?? ""}`)
        .join("|"),
    [sections],
  );

  useEffect(() => {
    if (!selectedType) return;

    const filtered = sections
      .filter((s) => s.type === selectedType)
      .map((section) => ({
        ...section,
        categoryMultilingual:
          section.categoryMultilingual ?? constructLocaleDict(() => ""),
      }));

    replace(filtered);
    let sortBy = filtered[0]?.sortByInsideDecoupage ?? "dateCreation:asc";
    if (
      target === "MULTIPRINTS" &&
      SORT_VALUES_EXCLUDED_FOR_MULTIPRINTS.includes(sortBy)
    ) {
      sortBy = "dateCreation:asc";
    }
    form.setValue("sortByInsideDecoupage", sortBy, { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sectionsFingerprint
  }, [selectedType, sectionsFingerprint, replace, form, target]);

  useEffect(() => {
    if (editingIndex == null) return;
    if (editingIndex >= fields.length) {
      setEditingIndex(null);
    }
  }, [fields.length, editingIndex]);

  useEffect(() => {
    if (!openLastAfterAppend || fields.length === 0) return;
    setEditingIndex(fields.length - 1);
    setOpenLastAfterAppend(false);
  }, [openLastAfterAppend, fields]);

  useEffect(() => {
    if (confirmingIndex == null) return;
    if (confirmingIndex >= fields.length) {
      setConfirmingIndex(null);
    }
  }, [confirmingIndex, fields.length]);

  const typeOptions = useMemo(
    () =>
      [
        { value: "creationDate", label: t("creationDate") },
        { value: "categories", label: t("oeuvresDescriptionCategorie") },
        { value: "series", label: t("oeuvresDescriptionSerie") },
        { value: "themes", label: t("oeuvresDescriptionTheme") },
        { value: "registrationNumber", label: t("registrationNumber") },
        {
          value: "type",
          label:
            target === "MULTIPRINTS"
              ? t("estampesMultiplesDescriptionType")
              : t("typeOeuvre"),
        },
      ].filter(
        (opt) =>
          target !== "MULTIPRINTS" ||
          !DECOUPAGE_TYPES_EXCLUDED_FOR_MULTIPRINTS.includes(opt.value),
      ),
    [t, target],
  );

  const decoupageValueColumnLabel = useMemo(() => {
    const selected = typeOptions.find((option) => option.value === selectedType);
    return selected?.label ?? t("categoryLabel");
  }, [selectedType, typeOptions, t]);

  const sortOptions = useMemo(() => {
    const all = [
      { value: "dateCreation:asc", label: t("sortByDateCreationAsc") },
      { value: "dateCreation:desc", label: t("sortByDateCreationDesc") },
      { value: "title:asc", label: t("sortByTitleAsc") },
      { value: "title:desc", label: t("sortByTitleDesc") },
      { value: "numeroEnregistrement:asc", label: t("sortByNumeroAsc") },
      { value: "numeroEnregistrement:desc", label: t("sortByNumeroDesc") },
      { value: "categorie:asc", label: t("sortByCategorieAsc") },
      { value: "categorie:desc", label: t("sortByCategorieDesc") },
      { value: "serie:asc", label: t("sortBySerieAsc") },
      { value: "serie:desc", label: t("sortBySerieDesc") },
      { value: "themes:asc", label: t("sortByThemesAsc") },
      { value: "themes:desc", label: t("sortByThemesDesc") },
      { value: "workType:asc", label: t("sortByWorkTypeAsc") },
      { value: "workType:desc", label: t("sortByWorkTypeDesc") },
      { value: "createdAt:asc", label: t("sortByCreatedAtAsc") },
      { value: "createdAt:desc", label: t("sortByCreatedAtDesc") },
    ];
    if (target === "MULTIPRINTS") {
      return all.filter(
        (opt) => !SORT_VALUES_EXCLUDED_FOR_MULTIPRINTS.includes(opt.value),
      );
    }
    return all;
  }, [target, t]);

  const addDecoupage = useCallback(() => {
    append({
      nameMultilingual: { fr: "", en: "", es: "", nl: "" },
      titleMultilingual: { fr: "", en: "", es: "", nl: "" },
      contentMultilingual: { fr: "", en: "", es: "", nl: "" },
      showContent: false,
      categoryMultilingual: constructLocaleDict(() => ""),
      startValue: "",
      endValue: "",
      categoryId: "",
      order: fields.length + 1,
      vignette: null,
      type: selectedType ?? "creationDate",
    });
    setOpenLastAfterAppend(true);
  }, [append, fields.length, selectedType]);

  const sectionCountLabel =
    fields.length === 0
      ? t("catalogueRaisonne.decoupage.noSectionYet", {
          defaultValue: "Aucune section pour l'instant",
        })
      : fields.length === 1
        ? t("catalogueRaisonne.decoupage.singleSectionCount", {
            defaultValue: "1 section",
          })
        : t("catalogueRaisonne.decoupage.multiSectionCount", {
            count: fields.length,
            defaultValue: "{{count}} sections",
          });

  return (
    <AF.Col>
      <div className="mb-6 rounded-xl border border-border bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          {t("decoupageType")}
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <AF.SelectField
            name="type"
            className="w-full"
            label={t("typeDecoupageSelectLabel")}
            options={typeOptions}
            placeholder={selectTypePlaceholder}
          />
          <AF.SelectField
            name="sortByInsideDecoupage"
            className="w-full"
            label={t("sortByInsideDecoupageLabel")}
            options={sortOptions}
            placeholder={t("selectType")}
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-border bg-white px-6 py-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">
              {t("catalogueRaisonne.decoupage.sectionsTitle", {
                defaultValue: "Sections du découpage",
              })}
            </h3>
            <p className="text-sm text-muted-foreground">{sectionCountLabel}</p>
          </div>
          <Button type="button" variant="outline" onClick={addDecoupage}>
            {t("addDecoupage")}
          </Button>
        </div>
        <div className="grid grid-cols-[80px_72px_1fr_1fr_1fr_auto] gap-3 border-b border-border bg-white px-6 py-3 text-xs font-semibold text-muted-foreground">
          <span>{t("order")}</span>
          <span>{t("vignetteImage")}</span>
          <span>{t("sectionName")}</span>
          <span>{decoupageValueColumnLabel}</span>
          <span>{t("titleSection")}</span>
          <span className="text-right">{t("actions")}</span>
        </div>
        <Sortable value={fields} onMove={handleMove}>
          <div className="divide-y divide-border bg-white">
            {fields.map((field, index) => {
              const sectionNamePath =
                `decoupages.${index}.nameMultilingual.${mainLang}` as Path<DecoupageFormValues>;
              const sectionTitlePath =
                `decoupages.${index}.titleMultilingual.${mainLang}` as Path<DecoupageFormValues>;
              const categoryPath =
                selectedType === "type"
                  ? (`decoupages.${index}.categoryMultilingual.${mainLang}` as Path<DecoupageFormValues>)
                  : (`decoupages.${index}.categoryId` as Path<DecoupageFormValues>);
              const sectionName =
                (watch(sectionNamePath) as string | undefined) ?? "";
              const sectionTitle =
                (watch(sectionTitlePath) as string | undefined) ?? "";
              const categoryValue =
                (watch(categoryPath) as string | undefined) ?? "";
              const startValue =
                (watch(`decoupages.${index}.startValue`) as string | undefined) ??
                "";
              const endValue =
                (watch(`decoupages.${index}.endValue`) as string | undefined) ??
                "";
              const decoupageValue = showCategoryFields
                ? categoryValue
                : [startValue, endValue].filter(Boolean).join(" – ");
              const hasContent =
                (watch(`decoupages.${index}.showContent`) as boolean | undefined) ??
                false;
              const canMoveUp = index > 0;
              const canMoveDown = index < fields.length - 1;

              return (
                <SortableItem key={field.id} value={field.id} className="min-w-0">
                  <div className="grid grid-cols-[80px_72px_1fr_1fr_1fr_auto] items-center gap-3 bg-white px-6 py-3">
                    <div className="flex items-center gap-2">
                      <SortableDragHandle
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 shrink-0"
                        aria-label={t("catalogueRaisonne.decoupage.reorder")}
                      >
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                      </SortableDragHandle>
                      <span className="text-sm font-medium">{index + 1}</span>
                      <div className="flex flex-col gap-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4"
                          disabled={!canMoveUp}
                          onClick={() =>
                            handleMove({ activeIndex: index, overIndex: index - 1 })
                          }
                          aria-label={t("catalogueRaisonne.decoupage.moveUp", {
                            defaultValue: "Monter la section",
                          })}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4"
                          disabled={!canMoveDown}
                          onClick={() =>
                            handleMove({ activeIndex: index, overIndex: index + 1 })
                          }
                          aria-label={t("catalogueRaisonne.decoupage.moveDown", {
                            defaultValue: "Descendre la section",
                          })}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-center">
                      <DecoupageTableVignette
                        index={index}
                        target={target}
                        cmsID={cmsID}
                        form={form}
                      />
                    </div>
                    <div className="truncate text-sm">
                      {sectionName || <span className="text-muted-foreground">—</span>}
                    </div>
                    <div className="truncate text-sm">
                      {decoupageValue || (
                        <span className="text-muted-foreground">
                          {showCategoryFields ? "—" : t("range")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 truncate text-sm">
                      <span className="truncate">
                        {sectionTitle || <span className="text-muted-foreground">—</span>}
                      </span>
                      {hasContent ? (
                        <Eye className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1 justify-end">
                      {confirmingIndex === index ? (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-red-600">
                            {t("catalogueRaisonne.decoupage.confirmDelete", {
                              defaultValue: "Supprimer ?",
                            })}
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              remove(index);
                              setConfirmingIndex(null);
                              if (editingIndex === index) {
                                setEditingIndex(null);
                              } else if (
                                editingIndex != null &&
                                editingIndex > index
                              ) {
                                setEditingIndex(editingIndex - 1);
                              }
                            }}
                          >
                            {t("yes", { defaultValue: "Oui" })}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmingIndex(null)}
                          >
                            {t("cancel")}
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingIndex(index)}
                            aria-label={t("catalogueRaisonne.decoupage.editSection", {
                              defaultValue: "Modifier la section",
                            })}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmingIndex(index)}
                            aria-label={t("catalogueRaisonne.decoupage.deleteSection", {
                              defaultValue: "Supprimer la section",
                            })}
                          >
                            <Trash2 className="h-4 w-4 text-red-500 hover:text-red-900 transition-colors" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </SortableItem>
              );
            })}
          </div>
        </Sortable>
        {fields.length === 0 ? (
          <div className="bg-white px-6 py-10 text-center">
            <p className="text-base font-semibold text-foreground">
              {t("catalogueRaisonne.decoupage.emptyStateTitle", {
                defaultValue: "Aucune section pour l'instant",
              })}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("catalogueRaisonne.decoupage.emptyStateDescription", {
                defaultValue:
                  "Créez la première section pour construire la grille de navigation.",
              })}
            </p>
            <Button className="mt-4" type="button" onClick={addDecoupage}>
              {t("addDecoupage")}
            </Button>
          </div>
        ) : null}
      </div>

      <StyledDrawer
        title={t("decoupage")}
        keepContentMounted
        sheetProps={{
          open: editingIndex != null,
          onOpenChange: (open) => {
            if (!open) setEditingIndex(null);
          },
        }}
        maxWidth="sm:max-w-3xl"
      >
        {editingIndex != null && editingIndex < fields.length ? (
          <DecoupageDrawerEditor
            index={editingIndex}
            form={form}
            target={target}
            cmsID={cmsID}
            showCategoryFields={showCategoryFields}
            selectedType={selectedType}
            categoriesData={categoriesData}
            isLoadingCategories={isLoadingCategories}
            categoryLabel={categoryLabel}
          />
        ) : null}
      </StyledDrawer>
    </AF.Col>
  );
};

export default DecoupageCatalogueRaisonneListForm;
